import { dispatchCreditStop } from '../services/apiClient';

// #1294: motivos de un corte por saldo decidido por el backend en check_stop. `renewal_unpaid`
// (#759 R): la renovación de Stripe sin cobrar; su aviso lleva a pagarla.
const CREDIT_STOP_REASONS = new Set(['credit_exhausted', 'debt_limit_reached', 'trial_expired', 'balance_unavailable', 'renewal_unpaid']);

/**
 * streamingSubscription — single source of truth for the streaming event
 * consumer, decoupled from any specific socket instance.
 *
 * Extracted from `useWebSocket.js` (#368 PR-A) so both socket-owning hooks —
 * `useWebSocket` (chat principal) and `useModularChat` (Role Builder /
 * Onboarding) — register the exact same listeners without duplicating the
 * wiring. The socket is injected, not captured, so the caller controls the
 * subscription lifecycle (register on socket create, cleanup on teardown).
 *
 * Events (all thread-scoped — filtered by `payload.threadId === targetThreadId`):
 *   stream-delta            → onDelta
 *   stream-tool-call-start  → onToolCallStart
 *   stream-agent-finish     → onAgentFinish
 *   stream-cancelled        → onCancelled
 *   stream-cancelled-plan   → onCancelledPlan  (#519: follow-up de un corte que
 *                              llevaba un plan en curso — trae el contador de
 *                              pasos desde `plan_execution`. Llega DESPUÉS de
 *                              stream-cancelled porque la escritura en la
 *                              auditoría no puede bloquear el corte, y puede no
 *                              llegar nunca: sin plan no hay evento)
 *   stream-error            → onError
 *   stream-snapshot         → onSnapshot   (rehidratación al (re)conectar — el
 *                              backend lo emite como PRIMER evento del socket
 *                              si el thread tiene un stream vivo)
 *
 * @param {import('socket.io-client').Socket|null} socket
 * @param {{
 *   onDelta?: (payload: any) => void,
 *   onToolCallStart?: (payload: any) => void,
 *   onAgentFinish?: (payload: any) => void,
 *   onCancelled?: (payload: any) => void,
 *   onCancelledPlan?: (payload: any) => void,
 *   onError?: (payload: any) => void,
 *   onSnapshot?: (payload: any) => void,
 * }} handlers
 * @param {string|null} targetThreadId
 * @returns {() => void} cleanup — removes the listeners registered by this call.
 */
export function subscribeStreamingEvents(socket, handlers, targetThreadId) {
  if (!socket) return () => {};

  const { onDelta, onToolCallStart, onAgentFinish, onCancelled, onCancelledPlan, onError, onSnapshot } = handlers || {};

  const filter = (handler) => (payload) => {
    if (!handler) return;
    if (payload?.threadId === targetThreadId) handler(payload);
  };

  // #1294 F4: un `stream-cancelled` con motivo de saldo lo decidió el backend en check_stop,
  // no el ⏹ del usuario. Aquí, y no en cada hook, porque los dos consumidores de streaming
  // pasan por esta función: el aviso único sale igual en el chat y en Role Builder/Onboarding.
  const handleCancelled = (payload) => {
    if (CREDIT_STOP_REASONS.has(payload?.reason)) {
      dispatchCreditStop({ reason: payload.reason, surface: 'chat' });
    }
    onCancelled?.(payload);
  };

  const wrapped = {
    'stream-delta':            filter(onDelta),
    'stream-tool-call-start':  filter(onToolCallStart),
    'stream-agent-finish':     filter(onAgentFinish),
    'stream-cancelled':        filter(handleCancelled),
    'stream-cancelled-plan':   filter(onCancelledPlan),
    'stream-error':            filter(onError),
    'stream-snapshot':         filter(onSnapshot),
  };

  Object.entries(wrapped).forEach(([event, handler]) => {
    socket.removeAllListeners(event);
    socket.on(event, handler);
  });

  return () => {
    Object.entries(wrapped).forEach(([event, handler]) => {
      socket.off(event, handler);
    });
  };
}
